import { apiClient } from './api';
import type {
  CardioSession,
  CreateCardioSessionInput,
  ApiCollection,
} from './types';

export interface CardioProgressSession {
  sessionId: number;
  date: string;
  exerciseName: string;
  durationMinutes?: number;
  heartRateAvg?: number;
  heartRatePeak?: number;
  distance?: string;
  distanceUnit?: string;
  avgSpeed?: string;
  caloriesBurned?: number;
  resultImage?: string;
}

export interface CardioProgressResponse {
  sessions: CardioProgressSession[];
}

export class CardioSessionService {
  private static readonly ENDPOINT = '/api/cardio_sessions';

  static async getAll(): Promise<CardioSession[]> {
    try {
      const endpoints = [
        '/api/cardio_sessions?pagination=false&order[sessionDate]=DESC',
        '/api/cardio-sessions?pagination=false&order[sessionDate]=DESC',
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await apiClient.get<ApiCollection<CardioSession>>(endpoint);
          const data = response.data['member'] || response.data || [];
          if (Array.isArray(data)) {
            return data;
          }
        } catch (err) {
          continue;
        }
      }
      return [];
    } catch (error) {
      console.error('Error fetching cardio sessions:', error);
      return [];
    }
  }

  static async getById(id: number): Promise<CardioSession> {
    const response = await apiClient.get<CardioSession>(`${this.ENDPOINT}/${id}`);
    return response.data;
  }

  static async create(session: CreateCardioSessionInput): Promise<CardioSession> {
    const endpoints = [
      '/api/cardio_sessions',
      '/api/cardio-sessions',
    ];

    let lastError: Error | null = null;
    for (const endpoint of endpoints) {
      try {
        const response = await apiClient.post<CardioSession>(endpoint, session);
        return response.data;
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        continue;
      }
    }
    throw lastError || new Error('Failed to create cardio session');
  }

  static async update(id: number, session: Partial<CreateCardioSessionInput>): Promise<CardioSession> {
    const response = await apiClient.patch<CardioSession>(`${this.ENDPOINT}/${id}`, session);
    return response.data;
  }

  static async delete(id: number): Promise<void> {
    await apiClient.delete(`${this.ENDPOINT}/${id}`);
  }

  static async getProgress(dateFrom?: string, dateTo?: string): Promise<CardioProgressResponse> {
    try {
      const queryParams = new URLSearchParams();
      if (dateFrom) queryParams.append('dateFrom', dateFrom);
      if (dateTo) queryParams.append('dateTo', dateTo);
      const qs = queryParams.toString();
      const suffix = qs ? '?' + qs : '';

      const endpoints = [
        '/api/cardio_progress' + suffix,
        '/api/cardio_progress' + suffix,
      ];

      for (const endpoint of endpoints) {
        try {
          const response = await apiClient.get<CardioProgressResponse>(endpoint);
          return response.data;
        } catch (err) {
          continue;
        }
      }
      // Return empty response instead of throwing
      return { sessions: [] };
    } catch (error) {
      console.error('Error fetching cardio progress:', error);
      // Return empty response instead of throwing
      return { sessions: [] };
    }
  }

  static async updateLogRow(
    id: number,
    payload: {
      cardioExerciseId?: number;
      durationMinutes?: number;
      distance?: number;
      distanceUnit?: string;
      avgSpeed?: number;
      heartRateAvg?: number;
      heartRatePeak?: number;
      caloriesBurned?: number;
    }
  ): Promise<{ row: CardioProgressSession & { cardioExerciseId?: number } }> {
    const response = await apiClient.put<{ row: CardioProgressSession & { cardioExerciseId?: number } }>(`/api/logs/cardio/${id}`, payload);
    return response.data;
  }
}
